import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

export type WorkspaceMode = 'treatment_record' | 'full_workspace';
export type DefaultSidebarState = 'expanded' | 'collapsed';
export type TreatmentRecordDrawerWidth = 'standard' | 'wide' | 'expanded';
// PRD.226 — controls whether the body of the patient page renders a
// Patient Overview section. The sidebar Patient Overview is always
// available regardless of this setting.
export type PatientOverviewDisplay = 'sidebar_only' | 'sidebar_and_body';

/** PowerSync sqlite row shape — booleans are stored as 0/1 integers. */
export interface PatientWorkspaceSettingsRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  workspace_mode: string | null;
  show_treatment_record: number | null;
  show_patient_timeline: number | null;
  default_sidebar_state: string | null;

  // Sidebar tab visibility
  sidebar_show_overview: number | null;
  sidebar_show_intake_form: number | null;
  sidebar_show_vitals: number | null;
  sidebar_show_allergies: number | null;
  sidebar_show_visits: number | null;
  sidebar_show_treatment_record: number | null;
  sidebar_show_contact: number | null;
  // PRD.203 — Patient Pharmacy as a first-class sidebar tab.
  sidebar_show_pharmacy: number | null;
  // PRD.262 — Patient Packages sidebar tab. Optional for backward-safe reads of
  // rows synced before this column existed (mapper falls back to default).
  sidebar_show_packages?: number | null;
  // PRD.286 — Files & Images sidebar tab (patient master file repository).
  // Optional for backward-safe reads of rows synced before this column existed.
  sidebar_show_files_images?: number | null;

  // Consultation / visit tab visibility
  consult_show_summary: number | null;
  consult_show_vitals: number | null;
  consult_show_charting: number | null;
  consult_show_notes: number | null;
  consult_show_prescriptions: number | null;
  consult_show_treatment_plan: number | null;
  consult_show_forms: number | null;
  consult_show_files: number | null;
  consult_show_invoices: number | null;
  consult_show_payments: number | null;
  // PRD.282 — remaining Visit Note sub-modules. Optional for backward-safe reads
  // of rows synced before these columns existed (mapper falls back to default).
  consult_show_chief_complaint?: number | null;
  consult_show_symptoms?: number | null;
  consult_show_diagnoses?: number | null;
  consult_show_pharmacy?: number | null;

  // Treatment Record Workspace (PRD.182)
  treatment_record_drawer_width: string | null;

  // PRD.226 — body-level Patient Overview placement.
  patient_overview_display: string | null;

  // PRD.237 — Queue workflow toggles. Optional in the row type because
  // older synced rows (before this PRD) won't include the columns; the
  // mapper falls back to defaults so the UI stays backward-safe.
  queue_action_pay_enabled?: number | null;
  queue_action_dispense_enabled?: number | null;

  created_at: string | null;
  updated_at: string | null;
}

/** UI-friendly settings shape for the page form state. */
export interface PatientWorkspaceSettingsUI {
  id: string;
  workspaceMode: WorkspaceMode;
  showTreatmentRecord: boolean;
  showPatientTimeline: boolean;
  defaultSidebarState: DefaultSidebarState;

  sidebarShowOverview: boolean;
  sidebarShowIntakeForm: boolean;
  sidebarShowVitals: boolean;
  sidebarShowAllergies: boolean;
  sidebarShowVisits: boolean;
  sidebarShowTreatmentRecord: boolean;
  sidebarShowContact: boolean;
  sidebarShowPharmacy: boolean;
  sidebarShowPackages: boolean;
  sidebarShowFilesImages: boolean;

  consultShowSummary: boolean;
  consultShowVitals: boolean;
  consultShowCharting: boolean;
  consultShowNotes: boolean;
  consultShowPrescriptions: boolean;
  consultShowTreatmentPlan: boolean;
  consultShowForms: boolean;
  consultShowFiles: boolean;
  consultShowInvoices: boolean;
  consultShowPayments: boolean;
  // PRD.282 — remaining Visit Note sub-modules.
  consultShowChiefComplaint: boolean;
  consultShowSymptoms: boolean;
  consultShowDiagnoses: boolean;
  consultShowPharmacy: boolean;

  // Treatment Record Workspace (PRD.182)
  treatmentRecordDrawerWidth: TreatmentRecordDrawerWidth;

  // PRD.226 — body-level Patient Overview placement.
  patientOverviewDisplay: PatientOverviewDisplay;

  // PRD.237 — Queue workflow toggles for optional Pay/Dispense actions.
  queueActionPayEnabled: boolean;
  queueActionDispenseEnabled: boolean;
}

/** Defaults aligned with the pg schema column defaults. */
export const PATIENT_WORKSPACE_DEFAULTS: Omit<PatientWorkspaceSettingsUI, 'id'> = {
  workspaceMode: 'full_workspace',
  showTreatmentRecord: true,
  showPatientTimeline: true,
  defaultSidebarState: 'expanded',

  sidebarShowOverview: true,
  sidebarShowIntakeForm: true,
  sidebarShowVitals: true,
  sidebarShowAllergies: true,
  sidebarShowVisits: true,
  sidebarShowTreatmentRecord: true,
  sidebarShowContact: true,
  sidebarShowPharmacy: true,
  sidebarShowPackages: true,
  // PRD.286 — enabled by default so every clinic gets the master file repository.
  sidebarShowFilesImages: true,

  consultShowSummary: true,
  consultShowVitals: true,
  consultShowCharting: true,
  consultShowNotes: true,
  consultShowPrescriptions: true,
  consultShowTreatmentPlan: true,
  consultShowForms: true,
  consultShowFiles: true,
  consultShowInvoices: true,
  consultShowPayments: true,
  // PRD.282 — default true preserves the prior "always allowed" behaviour.
  consultShowChiefComplaint: true,
  consultShowSymptoms: true,
  consultShowDiagnoses: true,
  consultShowPharmacy: true,

  treatmentRecordDrawerWidth: 'standard',

  // PRD.226 — default to body-visible to preserve the pre-PRD behaviour.
  // Clinics that prefer a work-first layout can switch to 'sidebar_only'.
  patientOverviewDisplay: 'sidebar_and_body',

  // PRD.237 — defaults true so existing clinics keep current PRD.236
  // Pay/Dispense visibility. Simpler workflows can disable either.
  queueActionPayEnabled: true,
  queueActionDispenseEnabled: true,
};

// ─── Queries ───

/**
 * Fetch the org-default Patient Workspace settings row (clinic_id IS NULL).
 */
export function qPatientWorkspaceSettings(): QuerySpec<[]> {
  return {
    key: ['patient-workspace-settings'] as const,
    sql: `SELECT * FROM patient_workspace_settings WHERE clinic_id IS NULL ORDER BY updated_at DESC LIMIT 1`,
    params: [],
    watch: ['patient_workspace_settings'],
  };
}

// ─── Mappers ───

function asBool(v: number | null, fallback: boolean): boolean {
  if (v == null) return fallback;
  return v === 1;
}

export function mapPatientWorkspaceSettingsToUI(
  row: PatientWorkspaceSettingsRow,
): PatientWorkspaceSettingsUI {
  const d = PATIENT_WORKSPACE_DEFAULTS;
  return {
    id: row.id,
    workspaceMode: (row.workspace_mode ?? d.workspaceMode) as WorkspaceMode,
    showTreatmentRecord: asBool(row.show_treatment_record, d.showTreatmentRecord),
    showPatientTimeline: asBool(row.show_patient_timeline, d.showPatientTimeline),
    defaultSidebarState: (row.default_sidebar_state ??
      d.defaultSidebarState) as DefaultSidebarState,

    sidebarShowOverview: asBool(row.sidebar_show_overview, d.sidebarShowOverview),
    sidebarShowIntakeForm: asBool(row.sidebar_show_intake_form, d.sidebarShowIntakeForm),
    sidebarShowVitals: asBool(row.sidebar_show_vitals, d.sidebarShowVitals),
    sidebarShowAllergies: asBool(row.sidebar_show_allergies, d.sidebarShowAllergies),
    sidebarShowVisits: asBool(row.sidebar_show_visits, d.sidebarShowVisits),
    sidebarShowTreatmentRecord: asBool(
      row.sidebar_show_treatment_record,
      d.sidebarShowTreatmentRecord,
    ),
    sidebarShowContact: asBool(row.sidebar_show_contact, d.sidebarShowContact),
    sidebarShowPharmacy: asBool(row.sidebar_show_pharmacy, d.sidebarShowPharmacy),
    sidebarShowPackages: asBool(row.sidebar_show_packages ?? null, d.sidebarShowPackages),
    sidebarShowFilesImages: asBool(row.sidebar_show_files_images ?? null, d.sidebarShowFilesImages),

    consultShowSummary: asBool(row.consult_show_summary, d.consultShowSummary),
    consultShowVitals: asBool(row.consult_show_vitals, d.consultShowVitals),
    consultShowCharting: asBool(row.consult_show_charting, d.consultShowCharting),
    consultShowNotes: asBool(row.consult_show_notes, d.consultShowNotes),
    consultShowPrescriptions: asBool(row.consult_show_prescriptions, d.consultShowPrescriptions),
    consultShowTreatmentPlan: asBool(row.consult_show_treatment_plan, d.consultShowTreatmentPlan),
    consultShowForms: asBool(row.consult_show_forms, d.consultShowForms),
    consultShowFiles: asBool(row.consult_show_files, d.consultShowFiles),
    consultShowInvoices: asBool(row.consult_show_invoices, d.consultShowInvoices),
    consultShowPayments: asBool(row.consult_show_payments, d.consultShowPayments),
    consultShowChiefComplaint: asBool(
      row.consult_show_chief_complaint ?? null,
      d.consultShowChiefComplaint,
    ),
    consultShowSymptoms: asBool(row.consult_show_symptoms ?? null, d.consultShowSymptoms),
    consultShowDiagnoses: asBool(row.consult_show_diagnoses ?? null, d.consultShowDiagnoses),
    consultShowPharmacy: asBool(row.consult_show_pharmacy ?? null, d.consultShowPharmacy),

    treatmentRecordDrawerWidth: (row.treatment_record_drawer_width ??
      d.treatmentRecordDrawerWidth) as TreatmentRecordDrawerWidth,

    patientOverviewDisplay: (row.patient_overview_display ??
      d.patientOverviewDisplay) as PatientOverviewDisplay,

    queueActionPayEnabled: asBool(row.queue_action_pay_enabled ?? null, d.queueActionPayEnabled),
    queueActionDispenseEnabled: asBool(
      row.queue_action_dispense_enabled ?? null,
      d.queueActionDispenseEnabled,
    ),
  };
}

export function mapUIToPatientWorkspaceSettingsColumns(ui: Omit<PatientWorkspaceSettingsUI, 'id'>) {
  const b = (v: boolean) => (v ? 1 : 0);
  return {
    workspace_mode: ui.workspaceMode,
    show_treatment_record: b(ui.showTreatmentRecord),
    show_patient_timeline: b(ui.showPatientTimeline),
    default_sidebar_state: ui.defaultSidebarState,

    sidebar_show_overview: b(ui.sidebarShowOverview),
    sidebar_show_intake_form: b(ui.sidebarShowIntakeForm),
    sidebar_show_vitals: b(ui.sidebarShowVitals),
    sidebar_show_allergies: b(ui.sidebarShowAllergies),
    sidebar_show_visits: b(ui.sidebarShowVisits),
    sidebar_show_treatment_record: b(ui.sidebarShowTreatmentRecord),
    sidebar_show_contact: b(ui.sidebarShowContact),
    sidebar_show_pharmacy: b(ui.sidebarShowPharmacy),
    sidebar_show_packages: b(ui.sidebarShowPackages),
    sidebar_show_files_images: b(ui.sidebarShowFilesImages),

    consult_show_summary: b(ui.consultShowSummary),
    consult_show_vitals: b(ui.consultShowVitals),
    consult_show_charting: b(ui.consultShowCharting),
    consult_show_notes: b(ui.consultShowNotes),
    consult_show_prescriptions: b(ui.consultShowPrescriptions),
    consult_show_treatment_plan: b(ui.consultShowTreatmentPlan),
    consult_show_forms: b(ui.consultShowForms),
    consult_show_files: b(ui.consultShowFiles),
    consult_show_invoices: b(ui.consultShowInvoices),
    consult_show_payments: b(ui.consultShowPayments),
    consult_show_chief_complaint: b(ui.consultShowChiefComplaint),
    consult_show_symptoms: b(ui.consultShowSymptoms),
    consult_show_diagnoses: b(ui.consultShowDiagnoses),
    consult_show_pharmacy: b(ui.consultShowPharmacy),

    treatment_record_drawer_width: ui.treatmentRecordDrawerWidth,

    patient_overview_display: ui.patientOverviewDisplay,

    queue_action_pay_enabled: b(ui.queueActionPayEnabled),
    queue_action_dispense_enabled: b(ui.queueActionDispenseEnabled),

    updated_at: new Date().toISOString(),
  };
}
